// Lightweight cache + prefetch helper for the booking page.
// Goal: when a visitor clicks "Book a Shoot" from the homepage the package
// cards must render instantly. We achieve that by:
//   1. Caching the /packages response in sessionStorage (5-minute TTL).
//   2. Letting the homepage warm that cache on hover/idle so the actual
//      navigation finds it already present.
import axios from "axios";
import { API } from "@/App";

const KEY_PREFIX = "propix3ls_packages_cache_v1::";
const TTL_MS = 5 * 60 * 1000; // 5 minutes — packages rarely change intra-session

const cacheKey = (currency) => `${KEY_PREFIX}${(currency || "GBP").toUpperCase()}`;

export function getCachedPackages(currency = "GBP") {
  try {
    const raw = sessionStorage.getItem(cacheKey(currency));
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || !Array.isArray(data)) return null;
    if (Date.now() - ts > TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function setCachedPackages(currency, data) {
  try {
    sessionStorage.setItem(
      cacheKey(currency),
      JSON.stringify({ ts: Date.now(), data })
    );
  } catch { /* sessionStorage may be full or disabled — non-fatal */ }
}

// In-flight de-duplication so concurrent prefetch + page-mount fetches
// share a single network request.
const inflight = new Map();

export function fetchPackages(currency = "GBP") {
  const cur = (currency || "GBP").toUpperCase();
  if (inflight.has(cur)) return inflight.get(cur);
  const p = axios
    .get(`${API}/packages?currency=${cur}`)
    .then((r) => {
      setCachedPackages(cur, r.data);
      return r.data;
    })
    .finally(() => inflight.delete(cur));
  inflight.set(cur, p);
  return p;
}

// Fire-and-forget warm-up. Safe to call multiple times — cache + in-flight
// dedupe ensure we never hammer the backend.
export function prefetchPackages(currency = "GBP") {
  if (getCachedPackages(currency)) return;
  fetchPackages(currency).catch(() => { /* silent — best effort */ });
}
